import { CUSTOMER_DETAIL_TABS } from '@/composables/customers/useCustomerDetailTabs';
import { formatCurrency } from '@/utils/numberFormat';
import { buildInitials, formatCodeName } from '@/utils/textValue';
import { formatDateShort } from '@/utils/datePresentation';
import { customerDetailGeneralMethods } from '@/composables/customers/customerDetail/customerDetailGeneralMethods';
import { personDetailMethods } from '@/composables/people/personDetailMethods';
import { contributorMethods } from '@/composables/contributors/contributorMethods';
import { buildRouteDetailLocation } from '@/composables/shared/useNavigation';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: customersText } = useDomainText('customers');
const { catalog: peopleText } = useDomainText('people');
const { catalog: billingDataText } = useDomainText('billingData');

const buildLatestSaleLabel = (latestSale = {}) => {
  return formatCodeName(latestSale?.code, latestSale?.name, latestSale?.label) || '-';
};

const buildLatestSaleRoute = (latestSale = {}) => {
  return buildRouteDetailLocation('saleDetail', 'saleId', latestSale?.id);
};

const buildLatestSaleMeta = (latestSale = {}) => {
  return `${customersText.detail.metrics.latestSaleMetaPrefix} ${formatDateShort(latestSale?.created_at)}`;
};

const buildLastInvoiceLabel = (lastInvoice = {}) => {
  const serial = String(lastInvoice?.serial || '').trim();
  const folio = String(lastInvoice?.folio || '').trim();
  return `${serial}${folio}`;
};

const buildLastInvoiceRoute = (lastInvoice = {}) => {
  return buildRouteDetailLocation('invoiceDetail', 'invoiceId', lastInvoice?.id);
};

const buildLastInvoiceMeta = (lastInvoice = {}) => {
  return `${customersText.detail.metrics.lastInvoiceMetaPrefix} ${formatDateShort(lastInvoice?.created_at)}`;
};

export const createCustomerDetailState = () => ({
  loading: false,
  customer: null,
  customerMetrics: null,
  customerOverview: null,
  persons: [],
  contributors: [],
  activeTab: 'summary',
  tabs: CUSTOMER_DETAIL_TABS,
  showCreateSaleModal: false,
  saleModalMode: 'create',
  saleModalKind: 'business',
  saleModalServiceFilterMode: 'standard',
  saleModalInitialData: null,
  createSaleSubmitting: false,
  isCustomerEditModalOpen: false,
  customerEditSectionKey: 'general',
  isPersonModalOpen: false,
  personModalMode: 'create',
  personModalInitialData: null,
  isPersonSubmitting: false,
  isContributorModalOpen: false,
  isContributorListModalOpen: false,
  contributorModalMode: 'create',
  contributorModalInitialData: null,
  isContributorSubmitting: false,
  isConfirmActionModalOpen: false,
  pendingActionType: '',
  pendingActionCustomer: null,
  pendingActionPerson: null,
  pendingActionContributor: null,
  isVisibilityModalOpen: false,
  visibilitySubmitting: false,
  visibilityUsersLoading: false,
  visibilityUsersOptions: [],
  isMergeModalOpen: false,
  mergeSubmitting: false,
  countryOptions: [],
  stateOptions: [],
  regimenOptions: [],
  usoCfdiOptions: [],
  paymentMethodOptions: [],
  paymentMethodTypeOptions: [],
});

export const customerDetailComputed = {
  customerId() {
    return Number(this.$route?.params?.customerId || 0);
  },
  customerName() {
    return String(this.customer?.name || '').trim();
  },
  customerIsActive() {
    return Number(this.customer?.active ?? 0) === 1;
  },
  customerLogo() {
    return String(this.customer?.logo_url || this.customer?.logo || '').trim();
  },
  customerInitials() {
    return buildInitials(this.customerName);
  },
  headerActions() {
    return [
      {
        key: 'edit-customer',
        label: customersText.detail.header.actions.editCustomer,
        icon: 'fa-pencil',
        variant: 'secondary',
      },
      {
        key: 'new-business',
        label: customersText.detail.header.actions.newBusiness,
        icon: 'fa-briefcase',
        variant: 'primary',
      },
      {
        key: 'new-retainer',
        label: customersText.detail.header.actions.newRetainer,
        icon: 'fa-refresh',
        variant: 'secondary',
      },
      {
        key: 'invoice-customer',
        label: customersText.detail.header.actions.invoiceCustomer,
        icon: 'fa-file-text-o',
        variant: 'success',
      },
    ];
  },
  detailMetrics() {
    const totalForecastValue = this.customerMetrics?.income_forecast;
    const totalBilledValue = this.customerMetrics?.income_billed;
    const latestSale = this.customerMetrics?.latest_sale && typeof this.customerMetrics.latest_sale === 'object'
      ? this.customerMetrics.latest_sale
      : {};
    const canViewTotal = this.customerMetrics?.capabilities?.can_view_total !== false;
    const canViewSales = this.customerMetrics?.capabilities?.can_view_sales !== false;
    const totalForecast = Number(totalForecastValue ?? this.customer?.income_forecast ?? this.customer?.cxc ?? 0);
    const totalBilled = Number(totalBilledValue ?? this.customer?.income_billed ?? this.customer?.total_billed ?? this.customer?.fxc ?? 0);
    const lastInvoiceFromMetrics = this.customerMetrics?.last_invoice && typeof this.customerMetrics.last_invoice === 'object'
      ? this.customerMetrics.last_invoice
      : {};
    const resolvedLastInvoiceLabel = buildLastInvoiceLabel(lastInvoiceFromMetrics);
    const lastInvoiceFolio = resolvedLastInvoiceLabel !== ''
      ? resolvedLastInvoiceLabel
      : String(this.customerMetrics?.last_invoice_folio || '').trim();
    const hasLastInvoice = Number(lastInvoiceFromMetrics?.id || 0) > 0 && lastInvoiceFolio !== '';

    return [
      {
        key: 'total-forecast',
        title: customersText.detail.metrics.totalForecast,
        value: canViewTotal ? formatCurrency(totalForecast) : customersText.detail.metrics.emptyDash,
        icon: 'fa-line-chart',
        iconBg: 'rgba(0, 178, 226, 0.12)',
        iconColor: '#00b2e2',
      },
      {
        key: 'total-billed',
        title: customersText.detail.metrics.totalBilled,
        value: canViewTotal ? formatCurrency(totalBilled) : customersText.detail.metrics.emptyDash,
        icon: 'fa-money',
        iconBg: 'rgba(0, 112, 205, 0.12)',
        iconColor: '#0070cd',
      },
      {
        key: 'last-invoice',
        title: customersText.detail.metrics.lastInvoice,
        value: lastInvoiceFolio !== '' ? lastInvoiceFolio : customersText.detail.metrics.lastInvoiceEmpty,
        valueRoute: hasLastInvoice ? buildLastInvoiceRoute(lastInvoiceFromMetrics) : null,
        valueLinkClass: hasLastInvoice ? 'kpi-value-link-customer' : '',
        valueMeta: hasLastInvoice ? buildLastInvoiceMeta(lastInvoiceFromMetrics) : '',
        valueScale: lastInvoiceFolio !== '' ? 1 : 0.65,
        icon: 'fa-file-text-o',
        iconBg: 'rgba(77, 191, 180, 0.12)',
        iconColor: '#4dbfb4',
      },
      {
        key: 'latest-sale',
        title: customersText.detail.metrics.latestSale,
        value: canViewSales ? buildLatestSaleLabel(latestSale) : customersText.detail.metrics.emptyDash,
        valueRoute: canViewSales ? buildLatestSaleRoute(latestSale) : null,
        valueLinkClass: 'kpi-value-link-customer',
        valueMeta: canViewSales ? buildLatestSaleMeta(latestSale) : '',
        truncateValue: true,
        valueScale: 0.5625,
        icon: 'fa-briefcase',
        iconBg: 'rgba(59, 212, 46, 0.12)',
        iconColor: '#3bd42e',
      },
    ];
  },
  defaultBillingData() {
    const rows = Array.isArray(this.contributors) ? this.contributors.slice() : [];
    if (rows.length === 0) {
      return null;
    }

    rows.sort((left, right) => {
      const leftDefault = Number(left?.default || 0);
      const rightDefault = Number(right?.default || 0);

      if (leftDefault !== rightDefault) {
        return rightDefault - leftDefault;
      }

      const leftId = Number(left?.id || 0);
      const rightId = Number(right?.id || 0);
      return rightId - leftId;
    });

    return rows[0] || null;
  },
  confirmActionTitle() {
    if (this.pendingActionType === 'archive-customer') {
      return customersText.detail.advancedOptionsConfirm.archive.title;
    }

    if (this.pendingActionType === 'unarchive-customer') {
      return customersText.detail.advancedOptionsConfirm.unarchive.title;
    }

    if (this.pendingActionType === 'delete-customer') {
      return customersText.confirm.delete.title;
    }

    if (this.pendingActionType === 'delete-contributor') {
      return billingDataText.confirm.titleDelete;
    }

    return peopleText.confirm.titleDelete;
  },
  confirmActionSubtitle() {
    if (this.pendingActionType === 'archive-customer') {
      return customersText.detail.advancedOptionsConfirm.archive.subtitle;
    }

    if (this.pendingActionType === 'unarchive-customer') {
      return customersText.detail.advancedOptionsConfirm.unarchive.subtitle;
    }

    if (this.pendingActionType === 'delete-customer') {
      return customersText.confirm.delete.subtitle;
    }

    if (this.pendingActionType === 'delete-contributor') {
      return billingDataText.confirm.subtitle;
    }

    return peopleText.confirm.subtitle;
  },
  confirmActionMessage() {
    if (this.pendingActionType === 'archive-customer') {
      const customerName = String(this.pendingActionCustomer?.name || '').trim() || customersText.common.noName;
      return customersText.detail.advancedOptionsConfirm.archive.message(customerName);
    }

    if (this.pendingActionType === 'unarchive-customer') {
      const customerName = String(this.pendingActionCustomer?.name || '').trim() || customersText.common.noName;
      return customersText.detail.advancedOptionsConfirm.unarchive.message(customerName);
    }

    if (this.pendingActionType === 'delete-customer') {
      const customerName = String(this.pendingActionCustomer?.name || '').trim() || customersText.common.noName;
      return customersText.confirm.delete.message(customerName);
    }

    if (this.pendingActionType === 'delete-contributor') {
      const contributorRfc = String(this.pendingActionContributor?.rfc || '').trim() || billingDataText.confirm.fallbackRfc;
      const contributorName = String(
        this.pendingActionContributor?.name
        || this.pendingActionContributor?.business_name
        || this.pendingActionContributor?.fiscal_name
        || ''
      ).trim() || billingDataText.confirm.fallbackFiscalName;

      return billingDataText.confirm.messageDelete({
        rfc: contributorRfc,
        fiscalName: contributorName,
      });
    }

    const personName = String(
      this.pendingActionPerson?.name
      || `${this.pendingActionPerson?.first_name || ''} ${this.pendingActionPerson?.last_name || ''}`
    ).trim() || peopleText.common.noName;

    return peopleText.confirm.messageDelete(personName.toUpperCase());
  },
  confirmActionButton() {
    if (this.pendingActionType === 'archive-customer') {
      return customersText.detail.advancedOptionsConfirm.archive.confirm;
    }

    if (this.pendingActionType === 'unarchive-customer') {
      return customersText.detail.advancedOptionsConfirm.unarchive.confirm;
    }

    if (this.pendingActionType === 'delete-customer') {
      return customersText.actions.common.delete;
    }

    if (this.pendingActionType === 'delete-contributor') {
      return billingDataText.confirm.confirmButton;
    }

    return peopleText.confirm.confirmButton;
  },
};

export const customerDetailMethods = {
  ...customerDetailGeneralMethods,
  ...personDetailMethods,
  ...contributorMethods,
};
